// ============================================================================
// Centralized React Query keys.
// ----------------------------------------------------------------------------
// One source of truth for every query key, shared by the domain hooks AND the
// live-sync invalidator (src/sync/useLiveSync). The live-sync layer maps a
// WebSocket `{entity}` to one of these key roots and invalidates it, so a
// change on another device refetches exactly the affected queries.
//
// Each root is a stable tuple prefix; partial-match invalidation (React Query's
// default) means invalidating e.g. `qk.clients.all` also invalidates
// `qk.clients.detail(id)` and `qk.clients.metrics(id)`.
// ============================================================================

export const qk = {
  auth: {
    me: ["auth", "me"] as const,
  },
  account: {
    detail: ["account"] as const,
    // The trainer's PUBLIC marketing profile + asset galleries (Wave 7). Hangs
    // off the `account` root so the `account` live-sync invalidation (a profile
    // / asset edit broadcasts `account`) refetches it without a reload.
    profile: ["account", "profile"] as const,
  },
  clients: {
    root: ["clients"] as const,
    list: (starred = false) => ["clients", "list", { starred }] as const,
    detail: (id: number) => ["clients", "detail", id] as const,
    measurements: (id: number) => ["clients", id, "measurements"] as const,
    metrics: (id: number) => ["clients", id, "metrics"] as const,
    sessions: (id: number) => ["clients", id, "sessions"] as const,
    goals: (id: number, activeOnly = true) =>
      ["clients", id, "goals", { activeOnly }] as const,
    // Trainer-side messaging (Wave 5): one owned client's thread + its unread
    // count, plus the roster-wide unread aggregate that feeds the per-row + nav
    // badges. The `before_id` pages keep distinct cache entries so "load older"
    // never clobbers the live tail. All hang off the `clients` root so they live
    // in the trainer realm and the `message` broadcast can invalidate them.
    messages: (id: number, beforeId?: number) =>
      ["clients", id, "messages", { beforeId: beforeId ?? null }] as const,
    messagesRoot: (id: number) => ["clients", id, "messages"] as const,
    messagesUnread: (id: number) =>
      ["clients", id, "messages-unread"] as const,
    // The trainer's roster-wide unread aggregate ({total, by_client}).
    unreadRoster: ["clients", "unread-roster"] as const,
    // Clients who have gone QUIET (Wave 6 inactivity scan). Hangs off the
    // `clients` root so the `client` live-sync invalidation (a login / self-log
    // touches last_active_at and broadcasts `client`) refetches it without a
    // reload — a client coming back online drops off this list live.
    inactive: ["clients", "inactive"] as const,
  },
  measurements: {
    root: ["measurements"] as const,
  },
  goals: {
    root: ["goals"] as const,
  },
  sessions: {
    root: ["sessions"] as const,
    detail: (id: number) => ["sessions", "detail", id] as const,
    calendar: (start?: string, end?: string) =>
      ["sessions", "calendar", { start: start ?? null, end: end ?? null }] as const,
  },
  packages: {
    root: ["packages"] as const,
    forClient: (id: number) => ["packages", "client", id] as const,
    detail: (id: number) => ["packages", "detail", id] as const,
    reminders: ["packages", "reminders"] as const,
  },
  // Trainer-side activities (provenance-tagged exercises). The calendar feed
  // merges into the dashboard/calendar grid; per-client list powers the
  // client-detail logged-activity view.
  activities: {
    root: ["activities"] as const,
    forClient: (clientId: number) => ["activities", "client", clientId] as const,
    calendar: (start?: string, end?: string) =>
      ["activities", "calendar", { start: start ?? null, end: end ?? null }] as const,
  },
  // Trainer-side nutrition (Wave 2): one client's targets + intake + water (the
  // detail-page Nutrition panel) and the all-clients meal-marker calendar feed
  // (the amber dots merged onto the trainer calendar). Read-only views — the
  // client owns the data; the trainer observes it.
  nutrition: {
    root: ["nutrition"] as const,
    forClient: (clientId: number, day?: string) =>
      ["nutrition", "client", clientId, { day: day ?? null }] as const,
    calendar: (start?: string, end?: string) =>
      ["nutrition", "calendar", { start: start ?? null, end: end ?? null }] as const,
  },
  // Trainer-set weekly goals (Wave 3): the goal for one client+week (targets +
  // exercise homework counts). Setting one overrides the auto nutrition targets
  // for that week, so the nutrition root is also invalidated on a change.
  weeklyGoals: {
    root: ["weekly-goals"] as const,
    forClient: (clientId: number, week?: string) =>
      ["weekly-goals", "client", clientId, { week: week ?? null }] as const,
    calendar: (start?: string, end?: string) =>
      ["weekly-goals", "calendar", { start: start ?? null, end: end ?? null }] as const,
  },
  exercises: {
    root: ["exercises"] as const,
    list: (filter?: unknown) => ["exercises", "list", filter ?? {}] as const,
    detail: (id: number) => ["exercises", "detail", id] as const,
  },
  favorites: {
    root: ["favorites"] as const,
  },
  templates: {
    root: ["templates"] as const,
    detail: (id: number) => ["templates", "detail", id] as const,
  },
  locations: {
    root: ["locations"] as const,
    detail: (id: number) => ["locations", "detail", id] as const,
    search: (q: string) => ["locations", "search", q] as const,
  },
  reports: {
    root: ["reports"] as const,
    client: (id: number, period: string) =>
      ["reports", "client", id, period] as const,
    business: (period: string) => ["reports", "business", period] as const,
  },
  // The trainer's own ICS calendar-subscription feed URL.
  calendar: {
    subscription: ["calendar", "subscription"] as const,
  },
  // Client portal realm (the SELF-SERVICE PWA). Entirely separate root from the
  // trainer keys above so the two auth contexts never share cache entries.
  client: {
    me: ["client", "me"] as const,
    dashboard: ["client", "dashboard"] as const,
    // The fancy dashboard aggregate (Wave 4): everything the rings + today hero
    // need in ONE call. Invalidated by the same writes as the dashboard so a log
    // re-renders the rings without a reload.
    home: ["client", "home"] as const,
    metrics: ["client", "metrics"] as const,
    measurements: ["client", "measurements"] as const,
    sessions: ["client", "sessions"] as const,
    goals: (activeOnly = true) => ["client", "goals", { activeOnly }] as const,
    report: (period: string) => ["client", "report", period] as const,
    calendar: ["client", "calendar", "subscription"] as const,
    // Self-service activities + the merged provenance calendar union.
    activities: ["client", "activities"] as const,
    calendarEntries: (start?: string, end?: string) =>
      ["client", "calendar-entries", { start: start ?? null, end: end ?? null }] as const,
    // THIS week's coach weekly goal + the client's progress checklist.
    weeklyGoal: ["client", "weekly-goal"] as const,
    // Nutrition (Wave 2): the day's meals + totals, daily water rows, and the
    // auto kcal/macro/water targets. Meals are day-scoped so the today view and
    // any other day never share a cache entry.
    meals: (day?: string) => ["client", "meals", { day: day ?? null }] as const,
    water: ["client", "water"] as const,
    nutritionTargets: ["client", "nutrition-targets"] as const,
    // Messaging (Wave 5): the client's single thread with their trainer + the
    // unread badge. The `before_id` pages keep distinct cache entries so "load
    // older" never clobbers the live tail.
    messages: (beforeId?: number) =>
      ["client", "messages", { beforeId: beforeId ?? null }] as const,
    messagesRoot: ["client", "messages"] as const,
    messagesUnread: ["client", "messages-unread"] as const,
  },
} as const;

/**
 * Maps a live-sync entity name (the `entity` field of a `{type:'changed'}`
 * WebSocket event) to the query-key roots that should be invalidated. Kept
 * here so keys and the sync mapping never drift apart. The entity strings
 * match what the backend Broadcaster emits (see viccofitness/api/* routers).
 */
export const ENTITY_INVALIDATIONS: Record<string, ReadonlyArray<readonly unknown[]>> = {
  client: [qk.clients.root, qk.sessions.root, qk.reports.root],
  measurement: [qk.clients.root, qk.reports.root],
  goal: [qk.clients.root, qk.goals.root, qk.reports.root],
  session: [qk.sessions.root, qk.clients.root, qk.reports.root],
  // A client logging/completing an activity broadcasts `activity` to the owning
  // trainer; invalidating the trainer activities root live-updates the merged
  // calendar (and the per-client logged-activity list on the detail page).
  activity: [qk.activities.root],
  // A client logging a meal / water broadcasts `meal` / `water` to the owning
  // trainer; invalidating the trainer nutrition root live-updates the per-client
  // Nutrition panel AND the amber meal-marker calendar feed, and clients root
  // keeps any client-derived summaries fresh. (The client realm invalidates its
  // own keys in client/hooks.ts.)
  meal: [qk.nutrition.root, qk.clients.root],
  water: [qk.nutrition.root, qk.clients.root],
  // A trainer setting a weekly goal broadcasts `weekly_goal`; invalidating the
  // weekly-goals root refreshes the goal form AND the trainer calendar's lime
  // week banner (the calendar feed hangs off the same root), the nutrition root
  // re-resolves the coach-vs-auto targets (a goal overrides the auto targets for
  // that week), and the client realm roots refresh the client's own progress card
  // + calendar banner so a goal set by the coach surfaces without a reload.
  weekly_goal: [
    qk.weeklyGoals.root,
    qk.nutrition.root,
    qk.client.weeklyGoal,
    qk.client.dashboard,
    ["client", "calendar-entries"],
  ],
  // A message (sent / read) broadcasts `message` to BOTH the trainer hub and the
  // affected client's portal sockets. On the TRAINER side, invalidating the
  // clients root refreshes every per-client thread (["clients", id, "messages"]),
  // its unread count, AND the roster-wide unread aggregate (all hang off the
  // `clients` root, so partial-match catches them) — live-updating the open
  // Messages tab, its "Seen" receipts, and the roster + tab unread badges. On the
  // CLIENT side, invalidating the client message + unread roots live-updates the
  // client's thread, receipts, and bottom-nav badge. Each side only holds the
  // keys for its own realm, so listing both here is safe (partial-match
  // invalidation skips absent keys).
  message: [
    qk.clients.root,
    qk.client.messagesRoot,
    qk.client.messagesUnread,
  ],
  set: [qk.sessions.root],
  cardio: [qk.sessions.root],
  exercise: [qk.exercises.root],
  favorite: [qk.favorites.root, qk.exercises.root],
  template: [qk.templates.root],
  location: [qk.locations.root],
  account: [qk.account.detail, qk.auth.me],
  package: [qk.packages.root, qk.clients.root, qk.sessions.root, qk.reports.root],
};
